import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid
} from '@mui/material';
import {
  AcUnit as FreezeIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  HourglassEmpty as PendingIcon,
  AccessTime as ActiveIcon
} from '@mui/icons-material';
import api from '../utils/api';

const MyPlanFreezes = () => {
  const [freezes, setFreezes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFreeze, setActiveFreeze] = useState(null);
  const [cancelDialog, setCancelDialog] = useState({ open: false, freezeId: null });

  useEffect(() => {
    loadFreezes();
    loadActiveFreeze();
  }, []);

  const loadFreezes = async () => {
    try {
      const response = await api.get('/plan-freeze/my-requests');
      setFreezes(response.data.data);
    } catch (error) {
      console.error('Error loading freeze history:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadActiveFreeze = async () => {
    try {
      const response = await api.get('/plan-freeze/current');
      setActiveFreeze(response.data.data);
    } catch (error) {
      console.error('Error loading active freeze:', error);
    }
  };

  const handleCancelRequest = async (freezeId) => {
    try {
      await api.delete(`/plan-freeze/${freezeId}/cancel`);
      setCancelDialog({ open: false, freezeId: null });
      loadFreezes();
      loadActiveFreeze();
    } catch (error) {
      console.error('Error cancelling freeze request:', error);
      alert(error.response?.data?.error || 'Failed to cancel freeze request');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      case 'active':
        return 'info';
      case 'completed':
        return 'default';
      case 'rejected':
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <PendingIcon fontSize="small" />;
      case 'approved':
      case 'completed':
        return <ApprovedIcon fontSize="small" />;
      case 'active':
        return <ActiveIcon fontSize="small" />;
      case 'rejected':
      case 'cancelled':
        return <RejectedIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDaysRemaining = (endDate) => {
    if (!endDate) return 0;
    const now = new Date();
    const end = new Date(endDate);
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FreezeIcon />
        My Plan Freezes
      </Typography>

      {/* Active Freeze Alert */}
      {activeFreeze && (
        <Alert
          severity="info"
          icon={<ActiveIcon />}
          sx={{ mb: 3 }}
        >
          <Typography variant="subtitle2" gutterBottom>
            <strong>Your plan is currently frozen</strong>
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">
                Freeze Start
              </Typography>
              <Typography variant="body2">
                {formatDate(activeFreeze.freezeStartDate)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">
                Freeze End
              </Typography>
              <Typography variant="body2">
                {formatDate(activeFreeze.freezeEndDate)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">
                Days Remaining
              </Typography>
              <Typography variant="body2" color="primary.main" fontWeight="medium">
                {getDaysRemaining(activeFreeze.freezeEndDate)} days
              </Typography>
            </Grid>
          </Grid>
          <Typography variant="body2" sx={{ mt: 2 }}>
            You cannot make bookings or join classes until {formatDate(activeFreeze.freezeEndDate)}.
          </Typography>
        </Alert>
      )}

      {/* Freeze History */}
      {freezes.length === 0 ? (
        <Alert severity="info">
          You haven't requested any plan freezes yet.
        </Alert>
      ) : (
        <List sx={{ bgcolor: 'background.paper' }}>
          {freezes.map((freeze, index) => (
            <React.Fragment key={freeze._id}>
              {index > 0 && <Divider />}
              <ListItem sx={{ display: 'block', py: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {freeze.plan?.name || 'Unknown Plan'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Requested: {formatDate(freeze.requestedAt)}
                    </Typography>
                  </Box>
                  <Chip
                    label={freeze.status}
                    color={getStatusColor(freeze.status)}
                    icon={getStatusIcon(freeze.status)}
                    size="small"
                  />
                </Box>

                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Duration
                    </Typography>
                    <Typography variant="body2">
                      {freeze.requestedDays} days
                    </Typography>
                  </Grid>

                  {freeze.status === 'active' && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          Freeze Period
                        </Typography>
                        <Typography variant="body2">
                          {formatDate(freeze.freezeStartDate)} - {formatDate(freeze.freezeEndDate)}
                        </Typography>
                      </Grid>
                    </>
                  )}

                  {freeze.status === 'approved' && freeze.approvedAt && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Approved
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(freeze.approvedAt)}
                        {freeze.approvedBy && ` by ${freeze.approvedBy.firstName} ${freeze.approvedBy.lastName}`}
                      </Typography>
                    </Grid>
                  )}

                  {freeze.status === 'rejected' && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          Rejected
                        </Typography>
                        <Typography variant="body2">
                          {formatDate(freeze.rejectedAt)}
                        </Typography>
                      </Grid>
                      {freeze.rejectionReason && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">
                            Reason
                          </Typography>
                          <Typography variant="body2" color="error.main">
                            {freeze.rejectionReason}
                          </Typography>
                        </Grid>
                      )}
                    </>
                  )}

                  {freeze.status === 'completed' && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Completed
                      </Typography>
                      <Typography variant="body2">
                        Freeze period ended
                      </Typography>
                    </Grid>
                  )}

                  {freeze.requestReason && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Your Reason
                      </Typography>
                      <Typography variant="body2">
                        {freeze.requestReason}
                      </Typography>
                    </Grid>
                  )}
                </Grid>

                {freeze.status === 'pending' && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => setCancelDialog({ open: true, freezeId: freeze._id })}
                    >
                      Cancel Request
                    </Button>
                  </Box>
                )}
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      )}

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={cancelDialog.open}
        onClose={() => setCancelDialog({ open: false, freezeId: null })}
      >
        <DialogTitle>Cancel Freeze Request?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel this freeze request? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog({ open: false, freezeId: null })}>
            No, Keep It
          </Button>
          <Button
            onClick={() => handleCancelRequest(cancelDialog.freezeId)}
            color="error"
            variant="contained"
          >
            Yes, Cancel Request
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyPlanFreezes;
